import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './EducationalResources.css';

const EducationalResources = () => {
  const [activeTab, setActiveTab] = useState('lens-types');

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <div className="educational-page">
      <Helmet>
        <title>Cataract Lens Types | Educational Resources for Cataract Surgery</title>
        <meta name="description" content="Learn about different cataract lens options including multifocal, monofocal, toric, and EDOF lenses. Comprehensive information about cataract surgery, recovery, and lens selection." />
        <meta name="keywords" content="cataract lens types, multifocal lens, monofocal lens, toric lens, EDOF lens, cataract surgery recovery, lens implant options" />
        <link rel="canonical" href="https://www.cataractlensadvisor.com/cataract-lens-types" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalWebPage",
              "headline": "Understanding Cataract Lens Types",
              "description": "Comprehensive information about different cataract lens options including monofocal, multifocal, EDOF, toric, and accommodating lenses.",
              "mainContentOfPage": {
                "@type": "WebPageElement",
                "cssSelector": ".educational-page"
              },
              "specialty": {
                "@type": "MedicalSpecialty",
                "name": "Ophthalmology"
              },
              "audience": {
                "@type": "MedicalAudience",
                "audienceType": "Patients"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="container">
        <h1 className="section-title">Cataract Lens Types & Educational Resources</h1>
        <p className="section-subtitle">Comprehensive information about cataract surgery and lens options to help you make informed decisions</p>
        
        <div className="intro-section">
          <p>
            Cataract surgery is one of the most common and successful medical procedures performed today. 
            Choosing the right intraocular lens (IOL) is a critical decision that can significantly impact 
            your vision quality after surgery. This guide provides detailed information about different 
            cataract lens types, including multifocal lenses, monofocal lenses, toric lenses for astigmatism, 
            and extended depth of focus (EDOF) lenses.
          </p>
        </div>
        
        <div className="tabs-container">
          <div className="tabs-navigation">
            <button 
              className={`tab-button ${activeTab === 'lens-types' ? 'active' : ''}`}
              onClick={() => handleTabChange('lens-types')}
            >
              Lens Types
            </button>
            <button 
              className={`tab-button ${activeTab === 'surgery' ? 'active' : ''}`}
              onClick={() => handleTabChange('surgery')}
            >
              Surgery & Recovery
            </button>
            <button 
              className={`tab-button ${activeTab === 'insurance' ? 'active' : ''}`}
              onClick={() => handleTabChange('insurance')}
            >
              Insurance & Costs
            </button>
            <button 
              className={`tab-button ${activeTab === 'lifestyle' ? 'active' : ''}`}
              onClick={() => handleTabChange('lifestyle')}
            >
              Lifestyle Considerations
            </button>
          </div>
          
          <div className="tab-content">
            {activeTab === 'lens-types' && (
              <div className="tab-pane">
                <h2>Understanding Cataract Lens Types</h2>
                
                <div className="lens-type-card">
                  <h3>Monofocal Lenses</h3>
                  <div className="lens-type-content">
                    <div className="lens-type-description">
                      <p>
                        Monofocal lenses are the most basic and common type of intraocular lens (IOL) used in cataract surgery. 
                        They provide clear vision at a single fixed distance, which is typically set for distance vision.
                      </p>
                      <h4>Key Features:</h4>
                      <ul>
                        <li>Provides clear vision at one distance (usually far)</li>
                        <li>Typically covered by insurance</li>
                        <li>Minimal visual disturbances (halos, glare)</li>
                        <li>Reading glasses usually required for near vision tasks</li>
                        <li>Good option for patients with certain eye conditions</li>
                      </ul>
                      <p>
                        Patients who choose monofocal lenses will typically need reading glasses for near vision tasks 
                        like reading or using a smartphone, unless they opt for a strategy called "monovision" where 
                        one eye is corrected for distance and the other for near vision.
                      </p>
                    </div>
                    <div className="lens-type-image">
                      <img src="/assets/monofocal-lens.png" alt="Monofocal intraocular lens for cataract surgery" className="lens-image" />
                    </div>
                  </div>
                </div>
                
                <div className="lens-type-card">
                  <h3>Multifocal Lenses</h3>
                  <div className="lens-type-content">
                    <div className="lens-type-description">
                      <p>
                        Multifocal lenses are designed to provide vision at multiple distances through different zones 
                        in the lens. They can reduce or eliminate the need for glasses after cataract surgery.
                      </p>
                      <h4>Key Features:</h4>
                      <ul>
                        <li>Provides vision at multiple distances (far, intermediate, near)</li>
                        <li>Reduces dependence on glasses</li>
                        <li>May cause more visual disturbances (halos, glare, reduced contrast)</li>
                        <li>Usually requires additional out-of-pocket cost</li>
                        <li>Not suitable for all patients, especially those with certain eye conditions</li>
                      </ul>
                      <p>
                        Multifocal lenses work by creating multiple focal points, allowing you to see at different 
                        distances. However, this design can cause some visual side effects, particularly in low light 
                        conditions. Patients may experience halos around lights at night or reduced contrast sensitivity.
                      </p>
                    </div>
                    <div className="lens-type-image">
                      <img src="/assets/multifocal-lens.png" alt="Multifocal intraocular lens for cataract surgery" className="lens-image" />
                    </div>
                  </div>
                </div>
                
                <div className="lens-type-card">
                  <h3>Extended Depth of Focus (EDOF) Lenses</h3>
                  <div className="lens-type-content">
                    <div className="lens-type-description">
                      <p>
                        EDOF lenses are a newer category that provides an extended range of vision from distance to 
                        intermediate, with less visual disturbances than traditional multifocal lenses.
                      </p>
                      <h4>Key Features:</h4>
                      <ul>
                        <li>Provides continuous vision from distance to intermediate</li>
                        <li>Fewer visual disturbances than multifocal lenses</li>
                        <li>May still require reading glasses for small print</li>
                        <li>Usually requires additional out-of-pocket cost</li>
                        <li>Good option for patients who want reduced dependence on glasses with fewer side effects</li>
                      </ul>
                      <p>
                        EDOF lenses use different optical technologies to extend the range of focus rather than creating 
                        distinct focal points. This design helps minimize visual disturbances while providing functional 
                        vision at multiple distances.
                      </p>
                    </div>
                    <div className="lens-type-image">
                      <img src="/assets/edof-lens.png" alt="Extended Depth of Focus intraocular lens for cataract surgery" className="lens-image" />
                    </div>
                  </div>
                </div>
                
                <div className="lens-type-card">
                  <h3>Toric Lenses</h3>
                  <div className="lens-type-content">
                    <div className="lens-type-description">
                      <p>
                        Toric lenses are specially designed to correct astigmatism along with cataracts. They can be 
                        monofocal, multifocal, or EDOF lenses with added astigmatism correction.
                      </p>
                      <h4>Key Features:</h4>
                      <ul>
                        <li>Corrects both cataracts and astigmatism</li>
                        <li>Reduces dependence on glasses for astigmatism correction</li>
                        <li>Available in monofocal, multifocal, and EDOF designs</li>
                        <li>Usually requires additional out-of-pocket cost</li>
                        <li>Requires precise positioning during surgery</li>
                      </ul>
                      <p>
                        Astigmatism is a common condition where the cornea has an irregular shape, causing blurred or 
                        distorted vision. Toric lenses have different powers in different meridians of the lens to 
                        correct this irregularity.
                      </p>
                    </div>
                    <div className="lens-type-image">
                      <img src="/assets/toric-lens.png" alt="Toric intraocular lens for cataract surgery with astigmatism correction" className="lens-image" />
                    </div>
                  </div>
                </div>
                
                <div className="lens-type-card">
                  <h3>Accommodating Lenses</h3>
                  <div className="lens-type-content">
                    <div className="lens-type-description">
                      <p>
                        Accommodating lenses are designed to move or change shape within the eye in response to the eye's 
                        focusing muscles, potentially providing a more natural range of vision.
                      </p>
                      <h4>Key Features:</h4>
                      <ul>
                        <li>Attempts to mimic the natural focusing ability of the eye</li>
                        <li>Provides good distance vision with some intermediate vision</li>
                        <li>Fewer visual disturbances than multifocal lenses</li>
                        <li>Usually requires additional out-of-pocket cost</li>
                        <li>Results can vary between patients</li>
                      </ul>
                      <p>
                        These lenses are designed to work with the eye's natural focusing mechanism. When the eye's 
                        ciliary muscle contracts, it allows the lens to move forward, changing the focus from distance 
                        to near objects. However, the degree of accommodation can vary between patients.
                      </p>
                    </div>
                    <div className="lens-type-image">
                      <img src="/assets/accommodating-lens.png" alt="Accommodating intraocular lens for cataract surgery" className="lens-image" />
                    </div>
                  </div>
                </div>
                
                <div className="cta-container">
                  <Link to="/cataract-lens-questionnaire" className="btn btn-primary">
                    Find Your Ideal Lens
                  </Link>
                </div>
              </div>
            )}
            
            {activeTab === 'surgery' && (
              <div className="tab-pane">
                <h2>Cataract Surgery and Recovery</h2>
                
                <div className="info-card">
                  <h3>Before Surgery</h3>
                  <p>
                    Before cataract surgery, your doctor will perform a comprehensive eye examination to evaluate your 
                    eye health and measure your eye for the correct lens power. This may include:
                  </p>
                  <ul>
                    <li>Visual acuity test</li>
                    <li>Slit-lamp examination</li>
                    <li>Retinal examination</li>
                    <li>Ocular coherence tomography (OCT)</li>
                    <li>Biometry to measure the eye's dimensions</li>
                  </ul>
                  <p>
                    You'll also discuss lens options with your doctor and make a decision based on your visual needs, 
                    lifestyle, and budget. Your doctor may ask you to temporarily stop taking certain medications and 
                    use antibiotic eye drops before surgery.
                  </p>
                </div>
                
                <div className="info-card">
                  <h3>During Surgery</h3>
                  <p>
                    Cataract surgery is typically an outpatient procedure that takes about 15-30 minutes per eye. 
                    Here's what happens during the procedure:
                  </p>
                  <ol>
                    <li>Your eye will be numbed with local anesthetic drops or injections</li>
                    <li>You may receive a mild sedative to help you relax</li>
                    <li>The surgeon makes a tiny incision in the cornea (the clear front part of the eye)</li>
                    <li>Using ultrasound (phacoemulsification), the surgeon breaks up the cloudy lens and removes it</li>
                    <li>The new artificial lens is inserted through the same incision</li>
                    <li>In most cases, the incision is so small that it seals itself without stitches</li>
                  </ol>
                  <p>
                    You'll be awake during the procedure but won't feel pain. You may see light and movement, but not 
                    the details of the surgery. Most people have one eye treated at a time, with the second eye done 
                    a few weeks later.
                  </p>
                </div>
                
                <div className="info-card">
                  <h3>Recovery Timeline</h3>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-marker">Day 1</div>
                      <div className="timeline-content">
                        <p>
                          Your vision may be blurry initially. You'll wear an eye shield, especially while sleeping. 
                          You'll start using prescribed eye drops. Avoid bending, lifting, or straining.
                        </p>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-marker">Week 1</div>
                      <div className="timeline-content">
                        <p>
                          Vision begins to improve, though it may fluctuate. Continue using eye drops as prescribed. 
                          Avoid getting water in your eye and strenuous activities. Wear sunglasses outdoors.
                        </p>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-marker">Weeks 2-4</div>
                      <div className="timeline-content">
                        <p>
                          Vision continues to stabilize. Most normal activities can be resumed, but avoid swimming 
                          and high-impact activities. Follow-up appointment to check healing.
                        </p>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-marker">1-3 Months</div>
                      <div className="timeline-content">
                        <p>
                          Full healing typically occurs. Vision stabilizes to its final state. Final prescription 
                          for glasses can be determined if needed. Most restrictions are lifted.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="info-card">
                  <h3>Potential Complications</h3>
                  <p>
                    Cataract surgery is generally very safe, but like any surgery, it carries some risks. 
                    Potential complications include:
                  </p>
                  <ul>
                    <li>Infection (very rare, about 0.1%)</li>
                    <li>Bleeding</li>
                    <li>Swelling</li>
                    <li>Retinal detachment</li>
                    <li>Secondary cataract (posterior capsule opacification)</li>
                    <li>Dislocation of the implanted lens</li>
                    <li>Increased eye pressure (glaucoma)</li>
                    <li>Droopy eyelid</li>
                  </ul>
                  <p>
                    Most complications can be treated successfully if caught early. It's important to follow 
                    your doctor's instructions and report any unusual symptoms promptly.
                  </p>
                </div>
                
                <div className="cta-container">
                  <Link to="/cataract-lens-questionnaire" className="btn btn-primary">
                    Find Your Ideal Lens
                  </Link>
                </div>
              </div>
            )}
            
            {activeTab === 'insurance' && (
              <div className="tab-pane">
                <h2>Insurance Coverage and Costs</h2>
                
                <div className="info-card">
                  <h3>Medicare Coverage</h3>
                  <p>
                    Medicare Part B covers cataract surgery if it's deemed medically necessary. Here's what's typically covered:
                  </p>
                  <ul>
                    <li>Conventional monofocal lenses</li>
                    <li>Surgeon fees</li>
                    <li>Facility fees</li>
                    <li>One pair of eyeglasses or contact lenses after surgery</li>
                  </ul>
                  <p>
                    Medicare beneficiaries are responsible for the Part B deductible and 20% of the Medicare-approved amount.
                  </p>
                  <div className="note-box">
                    <strong>Note:</strong> Medicare does not cover premium lenses (multifocal, EDOF, toric, accommodating) 
                    or advanced features. If you choose these options, you'll pay the difference out-of-pocket.
                  </div>
                </div>
                
                <div className="info-card">
                  <h3>Private Insurance</h3>
                  <p>
                    Private insurance coverage varies widely by provider and plan. Generally:
                  </p>
                  <ul>
                    <li>Most plans cover medically necessary cataract surgery</li>
                    <li>Coverage typically includes basic monofocal lenses</li>
                    <li>Premium lenses usually require out-of-pocket payment</li>
                    <li>Pre-authorization may be required</li>
                    <li>Deductibles, copays, and coinsurance will apply</li>
                  </ul>
                  <p>
                    Contact your insurance provider directly to understand your specific coverage details.
                  </p>
                </div>
                
                <div className="info-card">
                  <h3>Cost Breakdown</h3>
                  <div className="cost-table">
                    <div className="cost-row header">
                      <div className="cost-cell">Lens Type</div>
                      <div className="cost-cell">Typical Cost Range (per eye)</div>
                      <div className="cost-cell">Insurance Coverage</div>
                    </div>
                    <div className="cost-row">
                      <div className="cost-cell">Monofocal</div>
                      <div className="cost-cell">$3,000 - $5,000 total procedure</div>
                      <div className="cost-cell">Typically covered (after deductible/coinsurance)</div>
                    </div>
                    <div className="cost-row">
                      <div className="cost-cell">Toric (astigmatism-correcting)</div>
                      <div className="cost-cell">Additional $1,000 - $1,500</div>
                      <div className="cost-cell">Usually not covered</div>
                    </div>
                    <div className="cost-row">
                      <div className="cost-cell">Multifocal</div>
                      <div className="cost-cell">Additional $2,000 - $3,000</div>
                      <div className="cost-cell">Usually not covered</div>
                    </div>
                    <div className="cost-row">
                      <div className="cost-cell">EDOF</div>
                      <div className="cost-cell">Additional $2,000 - $3,000</div>
                      <div className="cost-cell">Usually not covered</div>
                    </div>
                    <div className="cost-row">
                      <div className="cost-cell">Accommodating</div>
                      <div className="cost-cell">Additional $1,500 - $2,500</div>
                      <div className="cost-cell">Usually not covered</div>
                    </div>
                  </div>
                  <p className="cost-disclaimer">
                    * Costs are approximate and can vary significantly by region, facility, and surgeon. 
                    These figures represent U.S. averages as of 2025.
                  </p>
                </div>
                
                <div className="info-card">
                  <h3>Payment Options</h3>
                  <p>
                    If you're considering premium lenses that aren't fully covered by insurance, 
                    many providers offer payment options:
                  </p>
                  <ul>
                    <li>
                      <strong>Flexible Spending Accounts (FSAs) or Health Savings Accounts (HSAs):</strong> 
                      Use pre-tax dollars to pay for eligible medical expenses
                    </li>
                    <li>
                      <strong>Payment Plans:</strong> Many surgical centers offer monthly payment plans
                    </li>
                    <li>
                      <strong>Medical Credit Cards:</strong> Specialized credit cards for healthcare expenses, 
                      often with promotional interest-free periods
                    </li>
                    <li>
                      <strong>Medicare Advantage Plans:</strong> Some plans may offer additional vision benefits
                    </li>
                  </ul>
                </div>
                
                <div className="cta-container">
                  <Link to="/cataract-lens-questionnaire" className="btn btn-primary">
                    Find Your Ideal Lens
                  </Link>
                </div>
              </div>
            )}
            
            {activeTab === 'lifestyle' && (
              <div className="tab-pane">
                <h2>Lifestyle Considerations for Lens Selection</h2>
                
                <div className="info-card">
                  <h3>Driving and Night Vision</h3>
                  <p>
                    How you drive, especially at night, should influence your lens choice:
                  </p>
                  <ul>
                    <li>
                      <strong>Frequent night driver:</strong> Consider monofocal or EDOF lenses, which typically 
                      cause fewer visual disturbances like halos and glare around lights
                    </li>
                    <li>
                      <strong>Occasional night driver:</strong> Multifocal lenses may be suitable, but be aware 
                      that some night vision effects are common
                    </li>
                    <li>
                      <strong>Professional driver:</strong> Discuss with your surgeon, as optimal distance vision 
                      and minimal visual artifacts are crucial
                    </li>
                  </ul>
                  <p>
                    If you have astigmatism and drive frequently, toric lenses may be worth considering to 
                    provide clearer vision without glasses.
                  </p>
                </div>
                
                <div className="info-card">
                  <h3>Reading and Close Work</h3>
                  <p>
                    Your reading habits and near vision needs are important factors:
                  </p>
                  <ul>
                    <li>
                      <strong>Avid reader:</strong> Multifocal lenses or monovision (one eye for distance, one for near) 
                      may reduce dependence on reading glasses
                    </li>
                    <li>
                      <strong>Digital device user:</strong> Consider EDOF or multifocal lenses that provide good 
                      intermediate vision for computer screens
                    </li>
                    <li>
                      <strong>Detailed close work (crafts, sewing):</strong> You may benefit from multifocal lenses, 
                      but some people prefer the clarity of monofocal lenses with reading glasses for precision tasks
                    </li>
                  </ul>
                </div>
                
                <div className="info-card">
                  <h3>Outdoor Activities</h3>
                  <p>
                    Active lifestyles require special consideration:
                  </p>
                  <ul>
                    <li>
                      <strong>Golfers:</strong> Need good distance vision with some intermediate vision for 
                      scorecards. EDOF or monofocal lenses often work well
                    </li>
                    <li>
                      <strong>Hikers/Travelers:</strong> May benefit from multifocal or EDOF lenses to see 
                      both distant landscapes and maps/guidebooks
                    </li>
                    <li>
                      <strong>Water sports:</strong> Consider how important it is to see without glasses while 
                      swimming or boating
                    </li>
                    <li>
                      <strong>Tennis/Ball sports:</strong> Require good distance and intermediate vision with 
                      minimal visual disturbances
                    </li>
                  </ul>
                </div>
                
                <div className="info-card">
                  <h3>Professional Requirements</h3>
                  <p>
                    Your occupation may influence your lens choice:
                  </p>
                  <ul>
                    <li>
                      <strong>Computer-based work:</strong> EDOF lenses often provide good vision at computer 
                      distance without glasses
                    </li>
                    <li>
                      <strong>Precision work (surgeons, jewelers):</strong> May prefer monofocal lenses with 
                      glasses for specific tasks due to optimal clarity
                    </li>
                    <li>
                      <strong>Artists/Designers:</strong> Consider how important color perception and contrast 
                      sensitivity are for your work
                    </li>
                    <li>
                      <strong>Public speakers/Performers:</strong> May want to reduce dependence on glasses 
                      with multifocal or EDOF lenses
                    </li>
                  </ul>
                </div>
                
                <div className="info-card">
                  <h3>Glasses Preference</h3>
                  <p>
                    Your willingness to wear glasses is a key factor:
                  </p>
                  <div className="preference-scale">
                    <div className="preference-option">
                      <h4>Strongly prefer no glasses</h4>
                      <p>Consider multifocal lenses, despite potential compromises in visual quality and higher cost</p>
                    </div>
                    <div className="preference-option">
                      <h4>Prefer minimal glasses use</h4>
                      <p>EDOF lenses or monovision may provide a good balance, possibly needing glasses only for reading</p>
                    </div>
                    <div className="preference-option">
                      <h4>Don't mind wearing glasses</h4>
                      <p>Monofocal lenses offer excellent clarity and are fully covered by insurance</p>
                    </div>
                  </div>
                </div>
                
                <div className="cta-container">
                  <Link to="/cataract-lens-questionnaire" className="btn btn-primary">
                    Find Your Ideal Lens
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationalResources;
